import { Vector3, Group } from "three";
import { Previewable, PreviewManager } from "../object/PreviewManager";
import {
  InputPointerType,
  InputEventType,
  InputMultiPointerEvent,
  ISwipeArgs,
  IPinchArgs,
} from "../utils/input-handler/interface";
import { Viewer } from "../viewer";
import * as Config from "../config/ViewerConfig";
import { IMeasurementPreviewCreation } from "./types";
import { generatePreviewMeasurmentId } from "./utils";
import { MarkupText } from "./MarkupText";
import { GroupWork } from "@mui/icons-material";

export class MultiplePointMeasurementCreationPreview
  implements IMeasurementPreviewCreation
{
  points: Vector3[];
  private _viewer: Viewer;
  private _preview: PreviewManager;
  private _pinIds: number[];
  private _lineId: number;
  private _txtGroup: Group;
  private _totalText: MarkupText;
  private _currText: MarkupText;
  private _textsOnLines: MarkupText[] = [];

  private _resolve: any;
  private _reject: any;

  private _id: number;

  private _isPointPickedOnStartDrag = false;
  private _textScale: Config.ITextScale;

  public get id() {
    return this._id;
  }

  constructor(
    viewer: Viewer,
    preview: PreviewManager,
    textScale: Config.ITextScale
  ) {
    this._viewer = viewer;
    this._preview = preview;
    this.points = [];
    this._pinIds = [];
    this._id = generatePreviewMeasurmentId.next().value as number;
    this._txtGroup = new Group();
    this._totalText = new MarkupText(textScale);
    this._totalText.textSize = Config.TEXT_SIZE;
    this._currText = new MarkupText(textScale);
    this._currText.textSize = Config.TEXT_SIZE;
    this._txtGroup.add(this._totalText.model);
    this._txtGroup.add(this._currText.model);
    this._viewer.getScene(Config.SCENE_TYPE.MEASUREMENT).add(this._txtGroup);
    this._textScale = textScale;
  }

  public startMeasurement(): Promise<Vector3[]> {
    return new Promise((resolve, reject) => {
      this._resolve = resolve;
      this._reject = reject;
      const previewables = this._preview.init([
        {
          type: Previewable.PIN,
          count: 1,
          color: Config.PREVIEW_MEASUREMENT_COLOR,
        },
        {
          type: Previewable.LINE,
          count: 1,
          color: Config.PREVIEW_MEASUREMENT_COLOR,
        },
      ]);
      this._pinIds.push(...previewables[0].ids);
      this._lineId = previewables[1].ids[0];
    });
  }

  public finishMeasurement(status = true, message = "") {
    if (status && this.points.length >= 2) {
      this._resolve([...this.points]);
    } else {
      this._reject({ message, id: this.id });
    }
    this.dispose();
  }

  public dispose() {
    this.points.length = 0;

    this._viewer.getScene(Config.SCENE_TYPE.MEASUREMENT).remove(this._txtGroup);

    this._totalText.model.dispose();
    this._currText.model.dispose();
    this._textsOnLines.forEach((x) => x.model.dispose());

    this._preview.dispose([...this._pinIds, this._lineId]);
    const event = new CustomEvent("onPointClick", {
      detail: { count: this.points.length },
    });
    document.dispatchEvent(event);
  }

  public reset() {
    this.points.length = 0;

    this._viewer.getScene(Config.SCENE_TYPE.MEASUREMENT).remove(this._txtGroup);

    this._totalText.model.dispose();
    this._currText.model.dispose();
    this._textsOnLines.forEach((x) => x.model.dispose());

    this._txtGroup = new Group();
    this._totalText = new MarkupText(this._textScale);
    this._totalText.textSize = Config.TEXT_SIZE;
    this._currText = new MarkupText(this._textScale);
    this._currText.textSize = Config.TEXT_SIZE;
    this._txtGroup.add(this._totalText.model);
    this._txtGroup.add(this._currText.model);
    this._viewer.getScene(Config.SCENE_TYPE.MEASUREMENT).add(this._txtGroup);
  }

  public unmark() {
    this._viewer.getScene(Config.SCENE_TYPE.MEASUREMENT).remove(this._txtGroup);

    this._totalText.model.dispose();
    this._currText.model.dispose();

    if (this.points.length >= 2) {
      let lastIndex = this._textsOnLines.length - 1;
      this._textsOnLines[lastIndex].model.dispose();
      this._textsOnLines.pop();
    }

    if (this._textsOnLines.length > 0) {
      let lastIndex = this._textsOnLines.length - 1;

      this._textsOnLines[lastIndex].model.dispose();
      this._textsOnLines.pop();
    }

    this._txtGroup = new Group();
    this._totalText = new MarkupText(this._textScale);
    this._totalText.textSize = Config.TEXT_SIZE;
    this._currText = new MarkupText(this._textScale);
    this._currText.textSize = Config.TEXT_SIZE;
    this._txtGroup.add(this._totalText.model);
    this._txtGroup.add(this._currText.model);
    this._viewer.getScene(Config.SCENE_TYPE.MEASUREMENT).add(this._txtGroup);
  }

  private updatePosition(point: Vector3) {
    //updates for the latest one
    const pinId = this._pinIds[this._pinIds.length - 1];
    this._preview.updatePreview(pinId, {
      position: point,
      type: Previewable.PIN,
      visible: true,
    });
    if (this.points.length > 0) {
      this._preview.updatePreview(this._lineId, {
        type: Previewable.LINE,
        points: [...this.points, point],
        visible: true,
      });
    }
  }

  public handlePointerEvent(
    pointer: { pointerType: InputPointerType; eventType: InputEventType },
    event: PointerEvent
  ): void {
    switch (pointer.eventType) {
      case InputEventType.POINTER_HOVER: //touch cannot hover
        this.onMouseHover(event);
        break;
      case InputEventType.CLICK: {
        this.onMouseClick(pointer, event);

        const currentPinId = this._pinIds[this._pinIds.length - 1];
        const pin = this._preview.getPin(currentPinId);
        if (!pin.visible) return;
        if (event.pointerType === "touch") {
          //shift the point downward
          let { clientX, clientY } = event;
          clientY += Config.TOUCH_OFFSET;
          const point = this._viewer.intersectPointCloud({ clientX, clientY });
          point && this.updatePosition(point);
        }
        if (pointer.pointerType === InputPointerType.PRIMARY) {
          const point = this._preview.getPinPosition(currentPinId);

          if (point) {
            this.points.push(point.clone());
            const event = new CustomEvent("onPointClick", {
              detail: { count: this.points.length },
            });
            document.dispatchEvent(event);
            this._viewer.setPointsToMeasurement(undefined, this.points);
            if (this.points.length === 2) {
              this.finishMeasurement();
            }
          }
        }

        if (pointer.pointerType === InputPointerType.SECONDARY) {
          // 右クリックで計測終了
          if (this.points.length >= 2) {
            this._viewer.finishMeasurementCreation();
          }
          // 2点以下の場合はundoする
          else {
            for (let i = 0; i <= this.points.length; ++i) {
              this._viewer.undoMeasurement();
            }
          }
          this.dispose();
        }
        break;
      }

      case InputEventType.START_DRAG: //Start drag cannot resolve, end drag can resolve
        let { clientX, clientY } = event;
        if (event.pointerType === "touch") {
          //shift the point downward
          clientY += Config.TOUCH_OFFSET;

          if (pointer.pointerType === InputPointerType.PRIMARY) {
            const point = this._viewer.intersectPointCloud({
              clientX,
              clientY,
            });
            if (point) {
              this._viewer.enableControl({ rotate: false });
              this.updatePosition(point);
              this._isPointPickedOnStartDrag = true;
            }
            // if (point) {
            //   this.points.push(point.clone());
            // }
          }
        }
        break;

      case InputEventType.DRAGGED: {
        // end drag is required to resolve
        let { clientX, clientY } = event;

        if (event.pointerType === "touch") {
          //shift the point downward
          if (this._isPointPickedOnStartDrag) {
            clientY += Config.TOUCH_OFFSET;
            if (pointer.pointerType === InputPointerType.PRIMARY) {
              const point = this._viewer.intersectPointCloud({
                clientX,
                clientY,
              });
              point && this.updatePosition(point);
            }
          }
        }

        break;
      }
      case InputEventType.END_DRAG: {
        if (pointer.pointerType === InputPointerType.PRIMARY) {
          this._viewer.enableControl({ pan: true, rotate: true });
          if (event.pointerType === "touch") {
            if (this._isPointPickedOnStartDrag) {
              this._isPointPickedOnStartDrag = false;

              const point = this._preview.getPinPosition(
                this._pinIds[this._pinIds.length - 1]
              );

              if (point) {
                this.points.push(point.clone());
                const event = new CustomEvent("onPointClick", {
                  detail: { count: this.points.length },
                });
                document.dispatchEvent(event);
                this._viewer.setPointsToMeasurement(undefined, this.points);

                const pins = this._preview.addPreviewable({
                  type: Previewable.PIN,
                  count: 1,
                  color: Config.PREVIEW_MEASUREMENT_COLOR,
                });
                this._pinIds.push(...pins.ids);
              }
            }
          }
        }
        break;
      }
    }
  }

  private onMouseHover(event: PointerEvent) {
    const point = this._viewer.intersectPointCloud(event);
    if (!point) return;

    this.updatePosition(point);

    if (this.points.length > 0) {
      const lastPt = this.points[this.points.length - 1];
      // update current distance text
      const currDistance = Math.abs(point.distanceTo(lastPt));
      this._currText.text = this.getDistanceText(currDistance);
      this._currText.position = this.calcCenter(point, lastPt);

      // update total distance text
      const totalFixedDistance = this.points.reduce<number>(
        (prev, curr, index) => {
          if (index === 0) return 0;
          return (prev += Math.abs(curr.distanceTo(this.points[index - 1])));
        },
        0
      );
      const totalDistance = totalFixedDistance + currDistance;
      this._totalText.text = "Σ：" + this.getDistanceText(totalDistance);
      this._totalText.position = point;
    }
  }

  private onMouseClick(
    pointer: { pointerType: InputPointerType; eventType: InputEventType },
    event: PointerEvent
  ) {
    //if possible click will resolve
    const currentPinId = this._pinIds[this._pinIds.length - 1];
    const pin = this._preview.getPin(currentPinId);
    if (!pin.visible) return;
    if (event.pointerType === "touch") {
      //shift the point downward
      let { clientX, clientY } = event;
      clientY += Config.TOUCH_OFFSET;
      const point = this._viewer.intersectPointCloud({ clientX, clientY });
      point && this.updatePosition(point);
    }
    if (pointer.pointerType === InputPointerType.PRIMARY) {
      const point = this._preview.getPinPosition(currentPinId);
      if (point) {
        this.points.push(point.clone());
        const event = new CustomEvent("onPointClick", {
          detail: { count: this.points.length },
        });
        document.dispatchEvent(event);
        this._viewer.setPointsToMeasurement(undefined, this.points);

        const pins = this._preview.addPreviewable({
          type: Previewable.PIN,
          count: 1,
          color: Config.PREVIEW_MEASUREMENT_COLOR,
        });
        this._pinIds.push(...pins.ids);
      }
    }
  }

  public handleMultiplePointerEvent(
    eventType: InputMultiPointerEvent,
    args: ISwipeArgs | IPinchArgs
  ): void {
    console.warn("Multiple Pointer not implemented");
    // throw new Error("Method not implemented.");
  }

  public setPoints(points: Vector3[]): void {
    this.points = points.map((p) => p.clone());
    this._viewer.getScene(Config.SCENE_TYPE.MEASUREMENT).remove(this._txtGroup);
    this._txtGroup = new Group();
    this._totalText = new MarkupText(this._textScale);
    this._totalText.textSize = Config.TEXT_SIZE;
    this._currText = new MarkupText(this._textScale);
    this._currText.textSize = Config.TEXT_SIZE;
    this._txtGroup.add(this._totalText.model);
    this._txtGroup.add(this._currText.model);
    this._viewer.getScene(Config.SCENE_TYPE.MEASUREMENT).add(this._txtGroup);

    if (this.points.length >= 2) {
      for (let i = 0; i < this.points.length - 1; ++i) {
        this.addTextOnPointFixed(i);
      }
    }

    console.log(points);

    if (this.points.length > this._pinIds.length) {
      const pins = this._preview.addPreviewable({
        type: Previewable.PIN,
        count: this.points.length - this._pinIds.length + 1,
        color: Config.PREVIEW_MEASUREMENT_COLOR,
      });
      this._pinIds.push(...pins.ids);
    } else if (this.points.length < this._pinIds.length) {
      const idsToDispose = this._pinIds.splice(this.points.length);
      this._preview.dispose(idsToDispose);
      //one for current hovering around
      const pins = this._preview.addPreviewable({
        type: Previewable.PIN,
        count: 1,
        color: Config.PREVIEW_MEASUREMENT_COLOR,
      });
      this._pinIds.push(...pins.ids);
    }

    for (let i = 0; i < this.points.length; ++i) {
      this._preview.updatePreview(this._pinIds[i], {
        position: this.points[i],
        type: Previewable.PIN,
        visible: true,
      });
    }
    if (this.points.length > 1) {
      this._preview.updatePreview(this._lineId, {
        type: Previewable.LINE,
        points: [...this.points],
        visible: true,
      });
    } else {
      this._preview.updatePreview(this._lineId, {
        type: Previewable.LINE,
        points: [],
        visible: false,
      });
    }
    const event = new CustomEvent("onPointClick", {
      detail: { count: this.points.length },
    });
    document.dispatchEvent(event);
    this._viewer.updateRenderer();
  }

  private addTextOnPointFixed(index: number) {
    const pt1 = this.points[index + 1].clone();
    const pt2 = this.points[index].clone();

    const textPointFixed = new MarkupText(this._textScale);
    textPointFixed.textSize = Config.TEXT_SIZE;
    textPointFixed.text = this.getDistanceText(Math.abs(pt1.distanceTo(pt2)));
    textPointFixed.position = this.calcCenter(pt1, pt2);
    this._textsOnLines = [...this._textsOnLines, textPointFixed];
    this._txtGroup.add(textPointFixed.model);
  }

  private calcCenter(pt1: Vector3, pt2: Vector3) {
    return pt1.clone().add(pt2.clone().sub(pt1).multiplyScalar(0.5));
  }

  private getDistanceText(distance: number) {
    return `${distance.toFixed(3)} m`;
  }
}
